import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, Sparkles } from "lucide-react";

/**
 * ExclusiveHuntPills
 * Renders a row of gold "Exclusive Hunt" teaser pills for private drops
 * whose host opted in to public visibility (`is_public_visibility=true`).
 * No clues, no code, no claim — just a tease that tells passers-by
 * "private hunts happen here, contact us to host yours."
 *
 * Drop in anywhere on a public page (home hero, Drops list, City landing).
 */
export default function ExclusiveHuntPills({ city = null, compact = false, className = "" }) {
  const [pins, setPins] = useState([]);

  useEffect(() => {
    api.get("/private/public-pins")
      .then(({ data }) => {
        let rows = data.pins || [];
        if (city) rows = rows.filter((p) => (p.city || "").toLowerCase() === city.toLowerCase());
        setPins(rows);
      })
      .catch(() => setPins([]));
  }, [city]);

  if (pins.length === 0) return null;

  if (compact) {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`} data-testid="exclusive-hunt-pills">
        {pins.map((p) => (
          <span
            key={p.event_id || p.drop_id}
            className="inline-flex items-center gap-1.5 px-2 py-1 border rounded-sm font-mono text-[10px] uppercase tracking-wider"
            style={{
              borderColor: "rgba(215,166,42,0.5)",
              background: "rgba(215,166,42,0.08)",
              color: "#D7A62A",
            }}
            title={`Private hunt — ${p.event_name}${p.client_name ? ` · ${p.client_name}` : ""}`}
            data-testid={`exclusive-pill-${p.event_id || p.drop_id}`}
          >
            <Sparkles size={11} /> Exclusive · {p.city}
          </span>
        ))}
      </div>
    );
  }

  return (
    <section className={`loot-card-flat p-5 ${className}`} data-testid="exclusive-hunt-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "#D7A62A" }}>Exclusive · invite-only</div>
          <h3 className="font-display uppercase text-2xl mt-1">
            Private hunts <span style={{ color: "#D7A62A" }}>happening now</span>
          </h3>
        </div>
        <Link
          to="/private/sales"
          className="btn-gold text-xs inline-flex items-center gap-2"
          data-testid="exclusive-hunt-book-cta"
        >
          Book yours
        </Link>
      </div>
      <div className="space-y-2">
        {pins.map((p) => (
          <div
            key={p.event_id || p.drop_id}
            className="flex items-center gap-3 p-3 border"
            style={{ borderColor: "rgba(215,166,42,0.25)", background: "rgba(215,166,42,0.04)" }}
            data-testid={`exclusive-row-${p.event_id || p.drop_id}`}
          >
            <Lock size={14} style={{ color: "#D7A62A" }} />
            <div className="flex-1">
              <div className="font-display text-lg">{p.event_name}</div>
              <div className="font-mono text-xs text-[#F5F2E8]/60">
                {p.client_name && <>{p.client_name} · </>}{p.city}
                {p.zone_description && <> · {p.zone_description}</>}
              </div>
            </div>
            <span
              className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5"
              style={{
                background: p.status === "live" ? "rgba(118,184,42,0.15)" : "rgba(215,166,42,0.15)",
                color: p.status === "live" ? "#76B82A" : "#D7A62A",
              }}
            >
              {p.status}
            </span>
          </div>
        ))}
      </div>
      <div className="font-mono text-[10px] text-[#F5F2E8]/40 mt-3">
        Codes only. Hunts run in cities you can't see on the public map.
      </div>
    </section>
  );
}
